import { Component, ElementRef, OnDestroy, OnInit, ViewChild  } from "@angular/core";
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { CustomerInformation } from 'src/models/customer';
import { ActivatedRoute, Router } from "@angular/router";
import { ApiService } from 'src/services/api.service';
import { map } from "rxjs/operators";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { faAngleDown, faCircle } from "@fortawesome/free-solid-svg-icons";
import { AppSettingsService } from "../app-settings/app-settings.service";
import { CustomerStateService } from "src/services/customer-state.service";
import { SiteInformation } from "src/models/site-information";
import { PassPurchaseService } from 'src/services/pass-purchase.service';
import { PageLoadingService } from "src/services/page-loading.service";
import { TextReceiptInformation } from "src/models/text-receipt-information";

@Component({
  selector: 'app-site-landing',
  templateUrl: './site-landing.component.html',
  styleUrls: ['./site-landing.component.scss']
})
export class SiteLandingComponent implements OnInit, OnDestroy {
  faAngleDown = faAngleDown;
  faCircle = faCircle;
  private readonly onDestroy$ = new Subject<void>();
  private readonly navKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp'];

  public customerId: Observable<string>
  public customerInformation: Observable<CustomerInformation>;

  public sites$: Observable<SiteInformation[]>;
  public inputValue: string;
  selectedIndex: number = -1;
  

  public isProduction: boolean = false;

  public explainerVideoLink: SafeResourceUrl | null = null;
  
  public footerLogo: SafeResourceUrl;

  public canContinue: boolean = false;

  public mobileDownload: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl("https://universal.dev.dencar.sancsoft.net/#/");
  public mobileDownloadExists: boolean = false;

  public favoredSite$: Observable<SiteInformation | null> 

  public favoredSiteName$: Observable<string | null>;

  public isLoading: boolean = false;
  
  public receiptSearchOpen: boolean = false;
  public receiptSearchNumber: string = '';
  public receiptSearchCode: string = '';
  public receiptSearchLoading: boolean = false;
  public receiptSearchError: string = '';
  public receiptSearchResult: TextReceiptInformation | null = null;
  public receiptSearchPaymentId: string | null = null;

  @ViewChild('aboutText') aboutTextElement: ElementRef;
  @ViewChild('explainerVideo') explainerVideoElement: ElementRef;

  constructor(
    private apiService: ApiService,
    public customerStateService: CustomerStateService,
    public passPurchaseService: PassPurchaseService,
    private router: Router,
    private route: ActivatedRoute,
    public sanitizer: DomSanitizer,
    private appSettingsService: AppSettingsService,
    private loadingService: PageLoadingService
  ) { 
      this.isProduction = this.appSettingsService.isProduction;
    }

  async ngOnInit() {
    this.customerStateService.receiptInformation = null;
    this.loadingService.pageLoaded = false;

    if (this.route.snapshot.queryParamMap.get('receiptSearch') === 'true') {
      this.openReceiptSearch();
    }

    this.favoredSiteName$ = this.customerStateService.favoredSite$.pipe(map((site) => {
      if(site){
        if(this.loadingService.pageLoaded == false){
          this.loadingService.pageLoaded = true;
        }
        return site.name;
      } else {
        return null;
      }
    }))
       
    var tempCustomerDashboard = await this.customerStateService.customerInfo
    this.customerInformation = await this.customerStateService.currentCustomerInfo$.pipe( 
      map(customerDashboard => {
        if (customerDashboard == null){
          customerDashboard = tempCustomerDashboard
        } else {
          this.footerLogo = this.sanitizer.bypassSecurityTrustUrl('data:image/svg;base64,' + customerDashboard.image);
          if(customerDashboard.mobileDownload != null){
            this.mobileDownloadExists = true;
            this.mobileDownload = this.sanitizer.bypassSecurityTrustUrl(customerDashboard.mobileDownload);
          }

          // populate the customer styles/video link
          if (customerDashboard.explainerVideoLink) {
            this.explainerVideoLink = this.getSafeExplainerVideoUrl(customerDashboard.explainerVideoLink);
          }

          if (customerDashboard.aboutUsDescription == null || customerDashboard.aboutUsDescription == "")
          {
            customerDashboard.aboutUsDescription = "Thank you for your monthly wash pass purchase. Your pass includes 15 washes per month. Passes automatically renew each month. Cancel/suspend your pass anytime in the app"
            this.adjustVideoSize()
          }
        }
      return customerDashboard;
    }));
  }

  async washPassButtonClick(){
    this.loadingService.pageLoaded = false;
    this.router.navigate([`/customer/${this.customerStateService.customerId}/passes`, "pass-selection"]);
  }

  toggleBackground(index: number): void {
    this.selectedIndex = index;
  }

  async membershipButtonClick() {
    this.router.navigate([`/customer/${this.customerStateService.customerId}/manage-my-membership/dashboard`]);
  }

  contactButtonClick() {
    this.router.navigate([`/customer/${this.customerStateService.customerId}/contact-us`]);
  }

  openReceiptSearch() {
    this.receiptSearchOpen = true;
    this.receiptSearchNumber = '';
    this.receiptSearchCode = '';
    this.receiptSearchLoading = false;
    this.receiptSearchError = '';
    this.receiptSearchResult = null;
    this.receiptSearchPaymentId = null;
  }

  onKeydownNums(event: KeyboardEvent){
    if(/[0-9]/.test(event.key) || this.navKeys.includes(event.key)){
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  onKeydownCode(event: KeyboardEvent){
    if(/[0-9a-zA-Z]/.test(event.key) || this.navKeys.includes(event.key)){
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  closeReceiptSearch() {
    this.customerStateService.receiptInformation = null;
    this.receiptSearchOpen = false;
  }

  async receiptSearchNext() {
    this.customerStateService.receiptInformation = null;
    if (this.receiptSearchCode.length !== 6 || this.receiptSearchLoading || this.receiptSearchNumber.length < 10) {
      return;
    }

    this.receiptSearchLoading = true;
    this.receiptSearchError = '';
    this.receiptSearchResult = null;
    this.receiptSearchPaymentId = null;

    try {
      const paymentId = (await firstValueFrom(this.apiService.getPaymentFromReceiptCode(this.receiptSearchCode, this.customerStateService.customerId, this.stripPhoneFormat(this.receiptSearchNumber)))).paymentId;
      if (!paymentId) {
        this.receiptSearchError = 'No receipt was found for that code.';
        return;
      }

      this.receiptSearchPaymentId = paymentId;
      this.receiptSearchResult = await firstValueFrom(this.apiService.setTextReceiptInformation(paymentId));
    } catch (error) {
      console.error('Error searching for receipt:', error);
      this.receiptSearchError = 'No receipt was found for that code.';
    } finally {
      this.receiptSearchLoading = false;
    }
  }

  formatPhone() {
    let num = new Array<string>(14);
    num[0] = '(';
    num[4] = ')';
    num[5] = ' ';
    num[9] = '-'; 
    let number = this.receiptSearchNumber;

    if(number.length > 0){
      let tempNum: string = '';
      for(let i=0; i<number.length; ++i){
        if(/[0-9]/.test(number[i])){
          tempNum += number[i];
        }
      }

      for(let j=0; j<tempNum.length; ++j){
        if(j<3){
          num[j+1] = tempNum[j];
        } else if(j<6) {
          num[j+3] = tempNum[j];
        } else {
          num[j+4] = tempNum[j];
        }
      }
      let nullIndex = num.findIndex(i => i == null);
      let len = nullIndex == -1 ? 14 : nullIndex < 7 ? 4 : nullIndex < 11 ? 9 : 14;
      if(nullIndex < 7 && nullIndex != -1){
        num[0] = null;
      }
      let finalNum: string = '';
      for(let i=0; i<len; ++i){
        if(num[i] != null){
          finalNum += num[i];
        }
      }
      
      this.receiptSearchNumber = finalNum;
    }
  }

  private stripPhoneFormat(phoneNumber: string): string{
    return phoneNumber.replace(/[^0-9]/g, '');
  }

  viewReceipt() {
    if (this.receiptSearchPaymentId) {
      this.customerStateService.receiptInformation = this.receiptSearchResult;
      this.router.navigate(['receipt', this.receiptSearchPaymentId]);
    }
  }

  termsButtonClick() {
    this.router.navigate([`/customer/${this.customerStateService.customerId}/terms-of-service`])
  }

  setNewActiveSiteClick(siteId: string, siteName: string){
    this.customerStateService.forceSetCurrentActiveSite(siteId);
    this.canContinue = true;
  }

  getCustomerIdFromSite(customerId: Observable<string>) {
    this.customerId = customerId;
    return customerId;
  }

  adjustVideoSize() {
    const aboutTextHeight = this.aboutTextElement.nativeElement.offsetHeight;
    const explainerVideoElement = this.explainerVideoElement.nativeElement;

    // Adjust the size of the video container based on the height of the text
    if (aboutTextHeight < explainerVideoElement.offsetHeight)
    {
      explainerVideoElement.style.height = explainerVideoElement.offsetHeight + 'px';
    }
    else
    {
      explainerVideoElement.style.height = aboutTextHeight + 'px';
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
  }

  private getSafeExplainerVideoUrl(videoUrl: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.toEmbedVideoUrl(videoUrl));
  }

  private toEmbedVideoUrl(videoUrl: string): string {
    try {
      const url = new URL(videoUrl);

      if (url.hostname.includes('youtube.com')) {
        if (url.pathname.startsWith('/embed/')) {
          return videoUrl;
        }

        const videoId = url.searchParams.get('v');

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }

      if (url.hostname.includes('youtu.be')) {
        const videoId = url.pathname.replace('/', '');

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }

      if (url.hostname.includes('vimeo.com')) {
        if (url.hostname.includes('player.vimeo.com')) {
          return videoUrl;
        }

        const parts = url.pathname.split('/').filter(Boolean);
        const videoId = parts[0];
        const hash = parts[1];

        if (videoId) {
          return hash 
            ? `https://player.vimeo.com/video/${videoId}?h=${hash}`
            : `https://player.vimeo.com/video/${videoId}`;
        }
      }

      return videoUrl;
    } catch {
      return videoUrl;
    }
  }
}
